import axios from 'axios';
import { TEST_API_BASE_URL, PRE_API_BASE_URL, BASE_URL } from '../utils/config';
import {
  getOrderId,
  clearToken,
  buildLoginUrl,
  isFromApp,
  normalizeRedirectAfterAuth,
  isLoginRedirectSuppressed
} from '@/biz/tool';
import { getUserLang } from '@/i18n/utils';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_ENV === 'staging') {
    return TEST_API_BASE_URL;
  }
  if (process.env.REACT_APP_ENV === 'pre') {
    return PRE_API_BASE_URL;
  }
  if (process.env.REACT_APP_ENV === 'production') {
    return BASE_URL;
  }

  if (process.env.REACT_APP_ENV === 'development') {
    if (process.env.REACT_APP_API_BASE_URL) {
      // 通过 copy .env.example .env 文件配置
      return process.env.REACT_APP_API_BASE_URL;
    }
    return TEST_API_BASE_URL;
  }

  // Default to production API URL or add other environments as needed
  return BASE_URL; // replace with actual production API URL
};

const API_BASE_URL = getApiBaseUrl();

const toError = (
  message: string,
  extra?: {
    status?: number;
    payload?: unknown;
    responseData?: unknown;
    originalError?: unknown;
  }
) => {
  const err = new Error(message);
  if (extra) {
    (err as any).status = extra.status;
    (err as any).payload = extra.payload;
    (err as any).responseData = extra.responseData;
    (err as any).originalError = extra.originalError;
  }
  return err;
};

axios.defaults.withCredentials = true;

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Accept-Language': getUserLang().detectedValue
  }
});

/** 与 AuthGuard 一致：Opportunities 仅 URL 带 id 时可免 session 发请求 */
const isDealsOpportunitiesOpenGuest = () => {
  if (typeof window === 'undefined') return false;
  const { pathname, search } = window.location;
  if (pathname !== '/deals/opportunities') return false;
  const q = search.startsWith('?') ? search.slice(1) : search;
  const id = new URLSearchParams(q).get('id');
  return Boolean(id?.trim());
};

const isGuestHome = () => {
  if (typeof window === 'undefined' || window.location.pathname !== '/') {
    return false;
  }
  return Boolean(new URLSearchParams(window.location.search).get('id')?.trim());
};

/** 与 AuthGuard 一致：邀请/订单详情页可免登录查看 */
const isGuestAccessibleDetailPage = () => {
  if (typeof window === 'undefined') return false;
  const { pathname } = window.location;
  return (
    pathname.startsWith('/campaign/invites/detail/') ||
    pathname.startsWith('/campaign/detail/')
  );
};

const shouldSkipLoginRedirect = () => {
  if (typeof window === 'undefined') return false;
  const { pathname, search } = window.location;
  const oauthInProgress =
    window.sessionStorage.getItem('__oauth_redirect_in_progress__') === '1';
  // OAuth/Clerk 回调建立 session 的短暂阶段，不应被接口鉴权重定向打断
  if (pathname === '/clerk') return true;
  if (pathname.startsWith('/app-auth/')) return true;
  if (pathname === '/auth/bridge') return true;
  // 邮件验证链接对未登录用户开放，接口请求和鉴权错误都不应触发登录跳转
  if (pathname === '/email-confirmed') return true;
  if (search.includes('__clerk_handshake')) return true;
  if (oauthInProgress) return true;
  if (isLoginRedirectSuppressed()) return true;
  if (isGuestHome()) return true;
  if (isDealsOpportunitiesOpenGuest()) return true;
  if (isGuestAccessibleDetailPage()) return true;
  return false;
};

axiosInstance.interceptors.request.use(
  async (config) => {
    const skipAuth = Boolean((config as any).skipAuth);
    //@ts-ignore
    if (
      !skipAuth &&
      //@ts-ignore
      !window?.Clerk?.session &&
      !shouldSkipLoginRedirect() &&
      window.location.pathname !== '/' &&
      window.location.pathname !== '/auth/bridge' &&
      !window.location.pathname.includes('/detail') &&
      !window.location.pathname.includes('/campaign/invites') &&
      !window.location.pathname.includes('/email-unsubscribe') &&
      !String(config.url ?? '').includes('/public/')
    ) {
      redirectToLogin('request_no_session');
    }
    if (skipAuth) {
      return config;
    }
    if (process.env.REACT_APP_ENV === 'production') {
      //@ts-ignore
      if (window?.Clerk?.session) {
        window.sessionStorage.removeItem('__oauth_redirect_in_progress__');
        //@ts-ignore
        config.headers.Authorization = `Bearer ${await window.Clerk.session.getToken()}`;
      }
    } else {
      //@ts-ignore
      if (window?.Clerk?.session) {
        window.sessionStorage.removeItem('__oauth_redirect_in_progress__');
        //@ts-ignore
        config.headers.Authorization = `Bearer ${await window.Clerk.session.getToken({ template: 'testing-template' })}`;
      }
    }
    return config;
  },
  (error) => {
    // 请求错误处理
    return Promise.reject(error);
  }
);

// 跳转去登录
const redirectToLogin = (reason?: string, ignoreGuestDetailPage = false) => {
  // 最后一道保护：OAuth/Clerk 回调阶段绝不允许跳登录页
  if (
    shouldSkipLoginRedirect() &&
    !(ignoreGuestDetailPage && isGuestAccessibleDetailPage())
  ) {
    return;
  }
  window.localStorage.setItem('auth_redirect', window.location.href);
  //
  window.localStorage.removeItem('token');
  window.localStorage.removeItem('profile');
  window.localStorage.removeItem('platform');

  if (isFromApp()) {
    const redirect = normalizeRedirectAfterAuth(
      window.location.pathname + window.location.search
    );
    const appParams = new URLSearchParams({
      from: 'app',
      redirect
    });
    window.location.href = `/login?${appParams.toString()}`;
    return;
  }

  window.location.href = buildLoginUrl();
};

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (axios.isAxiosError(error) && error.response) {
      if (Boolean((error.config as any)?.skipAuth)) {
        return Promise.reject(
          toError(
            error?.response?.data?.message ||
              error?.message ||
              'Request failed',
            {
              status: error?.response?.status,
              payload: error?.response?.data?.payload,
              responseData: error?.response?.data,
              originalError: error
            }
          )
        );
      }
      const url = error?.response?.config?.url || '';
      const orderId = getOrderId();
      const isOrderDetailGuestApi =
        isGuestAccessibleDetailPage() &&
        orderId &&
        (url === `/order/${orderId}/updateInfluencerTimezone` ||
          url === `/order/${orderId}/delivery_content`);

      //
      if (
        isOrderDetailGuestApi ||
        // 以下是 全局都会调用的接口
        url?.startsWith('/influencer/info') ||
        // 获取phyllo 平台
        url === '/auth/phyllo/activeWorkPlatforms' ||
        url === '/auth/phyllo/sdkConfig' ||
        (typeof window !== 'undefined' &&
          window.location.pathname.startsWith('/email-unsubscribe'))
      ) {
        // 在detail 调用这些接口 鉴权失败 不需要跳转登录。
        // 也不需要清空token
        if (
          url === `/order/${orderId}/updateInfluencerTimezone` ||
          url === `/order/${orderId}/delivery_content` ||
          url === '/auth/phyllo/activeWorkPlatforms' ||
          url === '/auth/phyllo/sdkConfig'
        ) {
          // 时区 没加去掉鉴权。
        } else {
          clearToken();
        }
        //
      } else {
        // token 权限不足(用了其他人的token)
        if (error.response.status === 401) {
          // token 鉴权失败
          const isOrderDetail = url?.startsWith('/order/detail/');
          setTimeout(() => {
            if (isOrderDetail || !shouldSkipLoginRedirect()) {
              redirectToLogin('response_401', isOrderDetail);
            }
          }, 1000);
          return Promise.reject(
            toError('401 Unauthorized', {
              status: 401,
              responseData: error?.response?.data,
              originalError: error
            })
          );
        } else if (error.response.status === 403) {
          // token 权限不足(用了其他人的token)
          setTimeout(() => {
            if (!shouldSkipLoginRedirect()) {
              redirectToLogin('response_403');
            }
          }, 1000);
          return Promise.reject(
            toError('403 Forbidden', {
              status: 403,
              payload: error?.response?.data?.payload,
              responseData: error?.response?.data,
              originalError: error
            })
          );
        }
      }

      return Promise.reject(
        toError(
          error?.response?.data?.message || error?.message || 'Request failed',
          {
            status: error?.response?.status,
            payload: error?.response?.data?.payload,
            responseData: error?.response?.data,
            originalError: error
          }
        )
      );
    }
    // return Promise.reject({ message: 'Unknown error', error }); // Internal Server Error
    return Promise.reject(
      toError('Server error. Please refresh the page and try again.', {
        originalError: error
      })
    ); // Internal Server Error
  }
);

export default axiosInstance;
